import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BottomBlur,
  FeedStage,
  FeedViewport,
  HeaderBackButton,
  Overlay,
  OverlayContent,
  OverlayHeader,
  OverlayMobileHeader,
  StyledLogo,
  TopBlur,
} from './MediaFeed.styles';
import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron.svg';
import { likeFeedItem, unlikeFeedItem } from '../../store/slices/feed/asyncThunks';
import { selectUser } from '../../store/slices/user/slice';
import ReelCommentsModal from '../Reels/ReelCommentsModal/ReelCommentsModal';
import ReelShareModal from '../Reels/ReelShareModal/ReelShareModal';
import ReelsFeedList from '../../pages/ReelsPage/components/ReelsFeedList';
import useReelComments from '../../pages/ReelsPage/hooks/useReelComments';
import useReelShare from '../../pages/ReelsPage/hooks/useReelShare';
import { mapFeedItemsToReels } from '../../utils/feed';
import { notifyError } from '../../utils/notify';
import { getSafeAreaInset, isTelegramWebApp, triggerHaptic } from '../../utils/telegram';

const MediaFeed = ({ mediaList, startIndex, onClose, onLoadMore, hasMore, maxReached, fetchMorePending }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const loadMoreRef = useRef(null);
  const previousStartIndexRef = useRef(startIndex);
  const reels = useMemo(() => mapFeedItemsToReels(mediaList), [mediaList]);
  const [targetReelId, setTargetReelId] = useState(reels[startIndex]?.id || null);
  const { top, bottom } = getSafeAreaInset();
  const canLoadMore = hasMore ?? !maxReached;
  const commentsModal = useReelComments({ reels, user });
  const shareModal = useReelShare(reels);

  useEffect(() => {
    if (previousStartIndexRef.current === startIndex) {
      return;
    }

    previousStartIndexRef.current = startIndex;
    setTargetReelId(reels[startIndex]?.id || null);
  }, [reels, startIndex]);

  useEffect(() => {
    if (!canLoadMore) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fetchMorePending) {
          onLoadMore?.();
        }
      },
      { root: null, threshold: 0.4 },
    );
    const target = loadMoreRef.current;

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [canLoadMore, fetchMorePending, onLoadMore, reels.length]);

  const onLikeToggle = async reel => {
    if (reel.likePending) {
      return;
    }

    triggerHaptic();

    try {
      if (reel.liked) {
        await dispatch(unlikeFeedItem({ feedItemId: reel.id })).unwrap();
      } else {
        await dispatch(likeFeedItem({ feedItemId: reel.id })).unwrap();
      }
    } catch (error) {
      notifyError(error.message || 'Unable to update the reel like right now.');
    }
  };

  return (
    <>
      <Overlay onClick={onClose}>
        <OverlayContent $top={top} $bottom={bottom}>
          <OverlayMobileHeader onClick={e => e.stopPropagation()}>
            <StyledLogo />
          </OverlayMobileHeader>
          <OverlayHeader onClick={e => e.stopPropagation()}>
            {isTelegramWebApp() ? (
              <div />
            ) : (
              <HeaderBackButton onClick={onClose}>
                <ChevronIcon />
                Back
              </HeaderBackButton>
            )}
            <StyledLogo />
          </OverlayHeader>
          <FeedViewport onClick={e => e.stopPropagation()}>
            <FeedStage>
              <TopBlur />
              <ReelsFeedList
                reels={reels}
                emptyStateMessage="No feed items available yet."
                onAvatarClick={onClose}
                onLikeToggle={onLikeToggle}
                onOpenComments={reel => {
                  if (reel.commentsAllowed) {
                    commentsModal.openComments(reel.id);
                  }
                }}
                onOpenShare={reel => shareModal.openShare(reel.id)}
                targetReelId={targetReelId}
                onTargetReelLocated={() => setTargetReelId(null)}
                hasMore={canLoadMore}
                loadMoreRef={loadMoreRef}
                fetchMorePending={fetchMorePending}
                reelOverlayBottomPadding="2.4rem"
                reelOverlayZIndex={4}
                showItemGradients={false}
                showAvatarButton={false}
              />
              <BottomBlur />
            </FeedStage>
          </FeedViewport>
        </OverlayContent>
      </Overlay>

      <ReelCommentsModal
        isOpen={commentsModal.isCommentsOpen}
        onClose={commentsModal.closeComments}
        commentsCount={commentsModal.commentsCount}
        comments={commentsModal.comments}
        isLoading={commentsModal.isLoading}
        isSubmitting={commentsModal.isSubmitting}
        composerPlaceholder={commentsModal.composerPlaceholder}
        replyTargetName={commentsModal.replyTargetName}
        commentText={commentsModal.commentText}
        onCommentTextChange={commentsModal.setCommentText}
        onCommentSubmit={commentsModal.onCommentSubmit}
        onCommentLikeToggle={commentsModal.onCommentLikeToggle}
        onReplyClick={commentsModal.onReplyClick}
        onCancelReply={commentsModal.onCancelReply}
        onDeleteComment={commentsModal.onDeleteComment}
        onLoadMoreComments={commentsModal.onLoadMoreComments}
        commentsHasMore={commentsModal.commentsHasMore}
        commentsFetchMorePending={commentsModal.commentsFetchMorePending}
        onLoadReplies={commentsModal.onLoadReplies}
        onLoadMoreReplies={commentsModal.onLoadMoreReplies}
      />
      <ReelShareModal
        isOpen={shareModal.isShareOpen}
        onClose={shareModal.closeShare}
        onShareOptionClick={shareModal.onShareOptionClick}
      />
    </>
  );
};

export default MediaFeed;
